import {CommandsContext} from 'context';
import * as React from 'react';
import {Command} from 'hooks';

export enum ActionType {
	ADD_COMMAND = 'ADD_COMMAND',
	REMOVE_COMMAND = 'REMOVE_COMMAND',
}

type State = Command[];

const initialState: State = [];

const areCommandsEquivalent = (a: Command, b: Command) => {
	return (
		a.key === b.key &&
		a.hidden === b.hidden &&
		a.disabled === b.disabled &&
		a.text === b.text &&
		a.title === b.title &&
		a.priority === b.priority &&
		a.farCommand === b.farCommand
	);
};

export function reducer(
	state: State,
	{type, command}: {type: ActionType; command: Command},
): State {
	switch (type) {
		case ActionType.ADD_COMMAND: {
			const existingIndex = state.findIndex(x => x.key === command.key);

			if (existingIndex === -1) {
				return [...state, command];
			}

			const existing = state[existingIndex];
			if (areCommandsEquivalent(existing, command)) {
				return state;
			}

			const next = [...state];
			next[existingIndex] = command;
			return next;
		}
		case ActionType.REMOVE_COMMAND: {
			const exists = state.some(x => x.key === command.key);
			if (!exists) {
				return state;
			}

			return state.filter(x => x.key !== command.key);
		}
		default:
			return state;
	}
}

export const CommandsProvider: React.FC<{children: React.ReactNode}> = ({
	children,
}) => {
	const [commands, dispatch] = React.useReducer(reducer, initialState);
	const removeCommand = React.useMemo(
		() => (command: Command) => {
			dispatch({type: ActionType.REMOVE_COMMAND, command});
		},
		[dispatch],
	);
	const addCommand = React.useMemo(
		() => (command: Command) => {
			dispatch({type: ActionType.ADD_COMMAND, command});

			return () => removeCommand(command);
		},
		[dispatch, removeCommand],
	);

	const contextValue = React.useMemo(
		() => ({commands, addCommand, removeCommand}),
		[commands, addCommand, removeCommand],
	);

	return (
		<CommandsContext.Provider value={contextValue}>
			{children}
		</CommandsContext.Provider>
	);
};
